// Intent (1.2.0): "check your inbox" — the app-side half of the magic-link
// login ("confirm and return", no deep links). Polls every 3 s until the user
// clicks the link mailed from opiekun@polfinance.pl; a manual 6-digit code
// entry covers mail clients where links are awkward. Mock: auto-verifies ~4 s.

import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { routeAfterLogin } from '@/store/auth-routing';
import { useAuthStore } from '@/store/auth-store';
import { colors, fonts, radius, spacing } from '@/theme/tokens';

const POLL_MS = 3_000;
const RESEND_COOLDOWN_S = 30;

export default function AuthEmailSentScreen() {
  const { t } = useTranslation();
  const pendingEmail = useAuthStore((s) => s.pendingEmail);
  const pollEmailOnce = useAuthStore((s) => s.pollEmailOnce);
  const submitEmailCode = useAuthStore((s) => s.submitEmailCode);
  const startEmailLogin = useAuthStore((s) => s.startEmailLogin);

  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_S);
  const doneRef = useRef(false);

  // The poll loop — the heart of "confirm and return".
  useEffect(() => {
    if (expired) return;
    const timer = setInterval(async () => {
      if (doneRef.current) return;
      const result = await pollEmailOnce();
      if (doneRef.current) return;
      if (result.status === 'OK') {
        doneRef.current = true;
        clearInterval(timer);
        routeAfterLogin();
      } else if (result.status === 'EXPIRED') {
        clearInterval(timer);
        setExpired(true);
      } else if (result.status === 'ERROR' && result.message) {
        clearInterval(timer);
        setError(result.message);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [expired, pollEmailOnce]);

  // Resend countdown (mirrors the SMS screen's 30 s convention).
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1_000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const onVerifyCode = async () => {
    setVerifying(true);
    setError(null);
    const result = await submitEmailCode(code.trim());
    setVerifying(false);
    if (result.ok) {
      doneRef.current = true;
      routeAfterLogin();
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  const onResend = async () => {
    if (!pendingEmail) {
      router.back();
      return;
    }
    setError(null);
    const result = await startEmailLogin(pendingEmail);
    if (result.ok) {
      setExpired(false);
      setResendIn(RESEND_COOLDOWN_S);
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        keyboardShouldPersistTaps="handled"
      >
        <ScreenHeader title={t('auth.loginTitle')} />

        <View style={{ alignItems: 'center', gap: spacing.md, marginTop: spacing.lg }}>
          <View
            style={{
              width: 88,
              height: 88,
              borderRadius: 44,
              backgroundColor: colors.primaryTile,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="mail-open-outline" size={40} color={colors.primary} />
          </View>
          <AppText variant="headlineLg" color={colors.navy} style={{ textAlign: 'center' }}>
            {expired ? t('auth.emailExpiredTitle') : t('auth.emailSentTitle')}
          </AppText>
          <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
            {expired
              ? t('auth.emailExpiredBody')
              : t('auth.emailSentBody', { email: pendingEmail ?? '' })}
          </AppText>
        </View>

        {!expired && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm }}>
            <ActivityIndicator color={colors.primary} />
            <AppText variant="labelLg">{t('auth.emailWaiting')}</AppText>
          </View>
        )}

        {!expired &&
          (showCode ? (
            <View style={{ gap: spacing.base }}>
              <AppText variant="labelMd" color={colors.primary}>
                {t('auth.emailCodeLabel')}
              </AppText>
              <TextInput
                value={code}
                onChangeText={(text) => setCode(text.replace(/\D/g, '').slice(0, 6))}
                placeholder="______"
                placeholderTextColor={colors.outlineVariant}
                keyboardType="number-pad"
                style={{
                  backgroundColor: colors.primaryTile,
                  borderRadius: radius.card,
                  paddingHorizontal: spacing.md,
                  minHeight: 56,
                  fontFamily: fonts.semiBold,
                  fontSize: 24,
                  letterSpacing: 8,
                  color: colors.navy,
                  textAlign: 'center',
                }}
              />
              <PrimaryButton
                label={t('auth.emailCodeVerify')}
                disabled={code.length !== 6 || verifying}
                onPress={onVerifyCode}
              />
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              onPress={() => setShowCode(true)}
              style={{ minHeight: 44, justifyContent: 'center' }}
            >
              <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
                {t('auth.emailCodeToggle')}
              </AppText>
            </Pressable>
          ))}

        {error && (
          <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
            {error}
          </AppText>
        )}

        <Pressable
          accessibilityRole="button"
          onPress={onResend}
          disabled={!expired && resendIn > 0}
          style={{ minHeight: 44, justifyContent: 'center', opacity: !expired && resendIn > 0 ? 0.5 : 1 }}
        >
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {!expired && resendIn > 0
              ? t('auth.emailResendIn', { s: resendIn })
              : t('auth.emailResend')}
          </AppText>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          onPress={() => router.back()}
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.emailChangeAddress')}
          </AppText>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
